import { React, useContext, useEffect, useState, useRef, useId } from "react";
import { AppContext } from "../App";
import { callApi, getInlineLoader } from "../Helpers";
import { FiFileText, FiUploadCloud, FiCheckCircle, FiArrowRight, FiArrowLeft } from "react-icons/fi";

export default function CreateApplication({ uuid }) {
  const appContext = useContext(AppContext);
  const scopeId = useId().replace(/:/g, "");

  /* Component States */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState(null);
  const [cvName, setCvName] = useState("");
  const [coverName, setCoverName] = useState("");
  const [certName, setCertName] = useState("");

  /* DOM Reference Tracking Blocks */
  const cvInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const certInputRef = useRef(null);

  async function init() {
    setReady(false);
    await Promise.all([
      (async () => {
        if (uuid) {
          await getData();
        }
      })(),
    ]);
    setReady(true);
  }

  async function getData() {
    await callApi('jobapplicant/get-job-data', { jobUuid: uuid }).then((response) => {
      if (response && response.status === 1) {
        setData(response.data);
      } else {
        appContext?.tellError?.('Could not fetch data');
      }
    }).catch(() => {
      appContext?.tellError?.('System error fetching job details');
    });
  }

  function handleFileChange(e, nameSetter) {
    if (e.target && e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      const reader = new FileReader();
      reader.onload = function (event) {
        nameSetter(file.name);
      };
      reader.readAsDataURL(file);
    }
  }

  async function handleSubmitApplication() {
    const selectedCv = cvInputRef.current?.files?.[0];
    const selectedCover = coverInputRef.current?.files?.[0];
    const selectedCert = certInputRef.current?.files?.[0];

    if (!selectedCv) return appContext?.tellError?.("Your Curriculum Vitae (CV) file attachment is required.");
    if (!selectedCover) return appContext?.tellError?.("A tailored Cover Letter file attachment is required.");
    if (!selectedCert) return appContext?.tellError?.("Certified Academic Certificates are required.");

    setLoading(true);
    await callApi(
      'jobapplicant/apply-for-job',
      {
        jobUuid: uuid,
        cv: selectedCv,
        coverLetter: selectedCover,
        certifiedCertificates: selectedCert
      }
    ).then(response => {
      if (response && response.status === 1) {
        appContext?.tellMessage?.(response.msg || 'Successful');
        
        setCvName("");
        setCoverName("");
        setCertName("");

        appContext?.navBack?.();
      } else {
        appContext?.tellError?.(response.msg || "Submission context error.");
      }
    }).catch(() => {
      appContext?.tellError?.("Network serialization interface timeout error.");
    });
    setLoading(false);
  }

  useEffect(() => {
    init();
  }, [uuid]);

  useEffect(() => {
    appContext?.setShowOverlayLoader?.(loading);

    return () => {
      appContext?.setShowOverlayLoader?.(false);
    };
  }, [loading]);

  if (ready) {
    return (
      <>
        <style>{`
          .${scopeId}-container {
            background-color: #091118;
            color: #f3f4f6;
            min-height: 100%;
            font-family: system-ui, -apple-system, sans-serif;
            padding: 24px;
          }

          /* --- Header Panel --- */
          .${scopeId}-header {
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            padding-bottom: 20px;
            margin-bottom: 24px;
          }

          .${scopeId}-title-group {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 6px;
          }

          .${scopeId}-back-btn {
            background: transparent;
            border: 1px solid rgba(255, 255, 255, 0.15);
            color: #ffffff;
            width: 32px;
            height: 32px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .${scopeId}-back-btn:hover {
            background: rgba(255, 255, 255, 0.05);
            border-color: #ffffff;
          }

          .${scopeId}-title {
            font-size: 1.35rem;
            font-weight: 800;
            color: #ffffff;
            margin: 0;
            letter-spacing: -0.02em;
          }

          .${scopeId}-target-job {
            font-size: 0.8rem;
            color: var(--secondaryColor, #fff);
            font-weight: 600;
            opacity: 0.8;
            margin: 0 0 4px 0;
            font-family: monospace;
          }

          .${scopeId}-subtitle {
            color: rgba(255, 255, 255, 0.5);
            font-size: 0.8rem;
            margin: 0;
          }

          /* --- Dropzone / File Upload Controls --- */
          .${scopeId}-upload-stack {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }

          .${scopeId}-upload-block {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .${scopeId}-label {
            font-size: 0.75rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: rgba(255, 255, 255, 0.6);
          }

          .${scopeId}-dropzone {
            border: 1px dashed rgba(255, 255, 255, 0.15);
            background: rgba(255, 255, 255, 0.01);
            border-radius: 4px;
            padding: 24px 16px;
            text-align: center;
            transition: all 0.2s ease;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }

          .${scopeId}-dropzone:hover {
            border-color: var(--secondaryColor, #ffffff);
            background: rgba(255, 255, 255, 0.03);
          }

          .${scopeId}-dropzone-active {
            border-color: #10b981;
            background: rgba(16, 185, 129, 0.02);
          }

          .${scopeId}-dz-title {
            font-size: 0.85rem;
            font-weight: 600;
            color: #ffffff;
            margin-top: 8px;
            margin-bottom: 4px;
          }

          .${scopeId}-dz-desc {
            font-size: 0.75rem;
            color: rgba(255, 255, 255, 0.4);
            margin: 0;
          }

          .${scopeId}-attached-pill {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
            border: 1px solid rgba(16, 185, 129, 0.2);
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            margin-top: 12px;
            max-width: 90%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          /* --- Submit Action Block --- */
          .${scopeId}-footer-action {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid rgba(255, 255, 255, 0.08);
          }

          .${scopeId}-submit-btn {
            width: 100%;
            padding: 14px;
            font-size: 0.8rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s ease;
            background: var(--text-primary, #ffffff);
            color: #050b11;
          }

          .${scopeId}-submit-btn:hover {
            filter: brightness(1.1);
            transform: translateY(-1px);
          }
        `}</style>

        <div className={`${scopeId}-container`}>
          
          {/* Section Header */}
          <header className={`${scopeId}-header`}>
            <div className={`${scopeId}-target-job`}>{data.title}</div>
            <div className={`${scopeId}-title-group`}>
              <button className={`${scopeId}-back-btn`} onClick={() => appContext?.navBack?.()}>
                <FiArrowLeft size={14} />
              </button>
              <h1 className={`${scopeId}-title`}>Submit Application</h1>
            </div>
            <p className={`${scopeId}-subtitle`}>Compile and push all target credentials to settle vacancy initialization.</p>
          </header>

          {/* Interactive File Pipeline Stacks */}
          <div className={`${scopeId}-upload-stack`}>
            
            {/* UPLOAD SLOT 1: Curriculum Vitae (CV) */}
            <div className={`${scopeId}-upload-block`}>
              <label className={`${scopeId}-label`}>Curriculum Vitae (CV)</label>
              <div 
                className={`${scopeId}-dropzone ${cvName ? `${scopeId}-dropzone-active` : ""}`} 
                onClick={() => cvInputRef.current.click()}
              >
                <FiUploadCloud size={22} style={{ color: cvName ? '#10b981' : 'rgba(255,255,255,0.4)' }} />
                <div className={`${scopeId}-dz-title`}>Deploy professional resume profile</div>
                <p className={`${scopeId}-dz-desc`}>Attach PDF</p>
                
                {cvName && (
                  <div className={`${scopeId}-attached-pill`}>
                    <FiCheckCircle size={12} />
                    <span>{cvName}</span>
                  </div>
                )}
              </div>
              <input type="file" ref={cvInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCvName)} />
            </div>

            {/* UPLOAD SLOT 2: Cover Letter */}
            <div className={`${scopeId}-upload-block`}>
              <label className={`${scopeId}-label`}>Cover Letter</label>
              <div 
                className={`${scopeId}-dropzone ${coverName ? `${scopeId}-dropzone-active` : ""}`} 
                onClick={() => coverInputRef.current.click()}
              >
                <FiUploadCloud size={22} style={{ color: coverName ? '#10b981' : 'rgba(255,255,255,0.4)' }} />
                <div className={`${scopeId}-dz-title`}>Attach explicit statement layout</div>
                <p className={`${scopeId}-dz-desc`}>Attach PDF</p>
                
                {coverName && (
                  <div className={`${scopeId}-attached-pill`}>
                    <FiCheckCircle size={12} />
                    <span>{coverName}</span>
                  </div>
                )}
              </div>
              <input type="file" ref={coverInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCoverName)} />
            </div>

            {/* UPLOAD SLOT 3: Certified Certificates */}
            <div className={`${scopeId}-upload-block`}>
              <label className={`${scopeId}-label`}>Certified Academic Certificates</label>
              <div 
                className={`${scopeId}-dropzone ${certName ? `${scopeId}-dropzone-active` : ""}`} 
                onClick={() => certInputRef.current.click()}
              >
                <FiUploadCloud size={22} style={{ color: certName ? '#10b981' : 'rgba(255,255,255,0.4)' }} />
                <div className={`${scopeId}-dz-title`}>Verified Certificates</div>
                <p className={`${scopeId}-dz-desc`}>Merged PDF packet recommended</p>
                
                {certName && (
                  <div className={`${scopeId}-attached-pill`}>
                    <FiCheckCircle size={12} />
                    <span>{certName}</span>
                  </div>
                )}
              </div>
              <input type="file" ref={certInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCertName)} />
            </div>

          </div>

          {/* Core Submission Interface Trigger */}
          <footer className={`${scopeId}-footer-action`}>
            <button 
              className={`${scopeId}-submit-btn`} 
              onClick={handleSubmitApplication}
              disabled={loading}
            >
              <span>Submit Application</span>
              <FiArrowRight size={14} />
            </button>
          </footer>

        </div>
      </>
    );
  } else {
    return (
      <div style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {getInlineLoader()}
      </div>
    );
  }
}